import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '../services/api'
import { Device, RepairOption } from '../types'
import { useCartStore } from '../stores/cartStore'
import toast from 'react-hot-toast'
import ExpressRepairModal from './ExpressRepairModal'
import { getSEOConfig, updateMetaTags, generateServiceStructuredData } from '../utils/seo'

interface RepairSelectorProps {
  device: Device
  onBack: () => void
}

const RepairSelector: React.FC<RepairSelectorProps> = ({ device, onBack }) => {
  const { addItem } = useCartStore()
  const [selectedRepair, setSelectedRepair] = useState<RepairOption | null>(null)
  const [showAllRepairs, setShowAllRepairs] = useState(false)
  const [showExpressModal, setShowExpressModal] = useState(false)
  const [quantity, setQuantity] = useState(1)

  // Scroll to top when component loads or device changes
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [device._id])

  const { data, isLoading, error } = useQuery({
    queryKey: ['repairs', device._id],
    queryFn: () => api.getRepairsByDevice(device._id)
  })

  // SEO Optimierung für spezifische Geräte
  useEffect(() => {
    const seoConfig = getSEOConfig(device.brand, device.deviceModel)
    updateMetaTags(seoConfig)

    // Strukturierte Daten für bessere Google-Indexierung hinzufügen
    if (data?.data?.repairs && data.data.repairs.length > 0) {
      const mainRepair = data.data.repairs[0]
      const structuredData = generateServiceStructuredData(
        device.brand,
        device.deviceModel,
        mainRepair.price
      )

      // Strukturierte Daten in den Head einfügen
      let script = document.querySelector('#device-structured-data') as HTMLScriptElement
      if (!script) {
        script = document.createElement('script')
        script.id = 'device-structured-data'
        script.type = 'application/ld+json'
        document.head.appendChild(script)
      }
      script.textContent = JSON.stringify(structuredData)
    }
  }, [device, data])

  const handleAddToCart = async (withExpress: boolean) => {
    if (!selectedRepair) return

    try {
      // Calculate total price
      let totalPrice = selectedRepair.price
      if (withExpress && selectedRepair.expressAvailable) {
        totalPrice += selectedRepair.expressSurcharge
      }

      addItem({
        deviceId: device._id,
        repairId: selectedRepair._id,
        quantity: quantity,
        price: totalPrice,
        title: selectedRepair.title,
        isExpress: withExpress && selectedRepair.expressAvailable,
        device: device,
        repair: selectedRepair
      })

      toast.success('Reparatur zum Warenkorb hinzugefügt!')
      setSelectedRepair(null)
      setQuantity(1)
    } catch (error) {
      toast.error('Fehler beim Hinzufügen zum Warenkorb')
    }
  }

  const handleAddToCartClick = () => {
    if (!selectedRepair) {
      toast.error('Bitte wähle zuerst eine Reparatur aus')
      return
    }

    // Check if express is available for this repair
    if (selectedRepair.expressAvailable && selectedRepair.expressSurcharge > 0) {
      // Show express modal instead of window.confirm
      setShowExpressModal(true)
    } else {
      handleAddToCart(false)
    }
  }

  const handleExpressDecision = (withExpress: boolean) => {
    setShowExpressModal(false)
    handleAddToCart(withExpress)
  }

  const increaseQuantity = () => {
    setQuantity(prev => Math.min(prev + 1, 10)) // Max 10 items
  }

  const decreaseQuantity = () => {
    setQuantity(prev => Math.max(prev - 1, 1)) // Min 1 item
  }

  if (isLoading) {
    return <div className="text-center py-8">Lade Reparaturen...</div>
  }

  if (error) {
    console.error('RepairSelector render error:', error)
    return (
      <div className="text-center py-8 text-indigo-500">
        <div className="mb-4">
          <button
            onClick={onBack}
            className="text-indigo-600 hover:text-indigo-700 mr-4"
          >
            ← Zurück
          </button>
        </div>
        <div>
          API-Fehler beim Laden der Reparaturen: {error instanceof Error ? error.message : String(error)}
          <br />
          <small>Gerät ID: {device._id}</small>
        </div>
      </div>
    )
  }

  const repairsData = data?.data?.repairs || []
  const repairs = Array.isArray(repairsData) ? repairsData : []

  // Show only first 6 repairs initially
  const visibleRepairs = showAllRepairs ? repairs : repairs.slice(0, 6)
  const remainingCount = repairs.length - 6

  if (repairs.length === 0) {
    return (
      <div className="text-center py-8 text-orange-500">
        <div className="mb-4">
          <button
            onClick={onBack}
            className="text-indigo-600 hover:text-indigo-700 mr-4"
          >
            ← Zurück
          </button>
        </div>
        <div>
          Keine Reparaturen für dieses Gerät verfügbar.
          <br />
          <small>Gerät: {device.brand} {device.deviceModel}</small>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center mb-6">
        <button
          onClick={onBack}
          className="text-indigo-600 hover:text-indigo-700 mr-4 flex items-center gap-2 font-medium"
        >
          ← Zurück zur Modellauswahl
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Device Image - Left Side (Sticky until "How it works" section) */}
        <div className="lg:col-span-4">
          <div className="sticky top-8 bg-white rounded-xl shadow-lg p-6 border border-gray-200">
            <div className="aspect-square w-full mb-4 flex items-center justify-center">
              {device.image ? (
                <img
                  src={device.image}
                  alt={device.deviceModel}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.parentElement!.innerHTML = `<div class="text-8xl flex items-center justify-center h-full">${
                      device.category === 'smartphone' ? '📱' :
                      device.category === 'tablet' ? '📋' :
                      device.category === 'laptop' ? '💻' :
                      device.category === 'smartwatch' ? '⌚' :
                      device.category === 'gaming' ? '🎮' : '📱'
                    }</div>`;
                  }}
                />
              ) : (
                <div className="text-8xl">
                  {device.category === 'smartphone' ? '📱' :
                   device.category === 'tablet' ? '📋' :
                   device.category === 'laptop' ? '💻' :
                   device.category === 'smartwatch' ? '⌚' :
                   device.category === 'gaming' ? '🎮' : '📱'}
                </div>
              )}
            </div>
            <div className="text-center border-t pt-4">
              <h2 className="text-xl font-bold mb-1">{device.deviceModel}</h2>
              <p className="text-gray-600">{device.brand}</p>
            </div>
          </div>
        </div>

        {/* Repair Options - Right Side */}
        <div className="lg:col-span-8">
          {/* Header with device info */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-orange-500 mb-4">
              {device.deviceModel} Reparatur
            </h1>

            {/* Trust indicators */}
            <div className="space-y-2 text-gray-700">
              <div className="flex items-center gap-2">
                <span className="text-indigo-600">✓</span>
                <span>+2.000 glückliche Kunden</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-indigo-600">✓</span>
                <span><strong>Kein Datenverlust</strong> Fotos, Apps, Daten bleiben erhalten</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-indigo-600">✓</span>
                <span>{device.deviceModel} Ersatzteile in originaler Qualität auf Lager</span>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <h2 className="text-2xl font-bold mb-2">Reparaturleistungen</h2>
            <p className="text-gray-600">Wähle die gewünschte Reparatur aus</p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="font-semibold text-lg mb-3">Reparatur</h3>
            <div className="space-y-2 mb-4">
              {visibleRepairs.map((repair: RepairOption) => (
                <label
                  key={repair._id}
                  className="flex items-center gap-3 py-2 cursor-pointer hover:bg-gray-50 rounded transition-colors"
                >
                  <input
                    type="radio"
                    name="repair"
                    checked={selectedRepair?._id === repair._id}
                    onChange={() => setSelectedRepair(repair)}
                    className="w-5 h-5 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                  />
                  <div className="flex-1 flex items-center justify-between">
                    <span className="font-medium text-gray-900">- {repair.title}</span>
                    <span className="font-bold text-orange-500 ml-4">
                      {repair.price.toFixed(2)} €
                    </span>
                  </div>
                </label>
              ))}
            </div>

            {/* Show More Button */}
            {!showAllRepairs && remainingCount > 0 && (
              <button
                onClick={() => setShowAllRepairs(true)}
                className="w-full text-center py-2 text-orange-500 font-semibold hover:text-orange-600 transition-colors"
              >
                +{remainingCount} More
              </button>
            )}

            {showAllRepairs && repairs.length > 6 && (
              <button
                onClick={() => setShowAllRepairs(false)}
                className="w-full text-center py-2 text-orange-500 font-semibold hover:text-orange-600 transition-colors"
              >
                Weniger anzeigen
              </button>
            )}

            {/* Repair Time Info - Always visible */}
            <div className="mb-4 p-3 bg-orange-50 rounded-lg border border-orange-200 mt-4">
              <p className="text-sm text-orange-700">
                Reparaturzeit: 1 - 2 Arbeitstage
              </p>
              <p className="text-sm text-orange-700 mt-1">
                Same Day Reparatur im Warenkorb verfügbar
              </p>
            </div>

            {/* Quantity and Add to Cart */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <button
                  className="w-8 h-8 flex items-center justify-center border-2 border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={decreaseQuantity}
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <span className="w-8 text-center font-semibold text-gray-900">
                  {quantity}
                </span>
                <button
                  className="w-8 h-8 flex items-center justify-center border-2 border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={increaseQuantity}
                  disabled={quantity >= 10}
                >
                  +
                </button>
              </div>

              <button
                onClick={handleAddToCartClick}
                disabled={!selectedRepair}
                className={`flex-1 py-3 px-8 rounded-lg font-semibold transition-colors ${
                  selectedRepair
                    ? 'bg-orange-400 text-white hover:bg-orange-500'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                In den Warenkorb
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* How it works section - Full width below the grid */}
      <div className="mt-12">
        <h2 className="text-3xl font-bold text-orange-500 text-center mb-4">
          So einfach ist PhoneMedic
        </h2>
        <p className="text-center text-gray-700 mb-8">
          Mit nur wenigen Klicks ist die {device.deviceModel} Reparatur bestellt – einfacher und komfortabler geht es nicht!
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Step 1 */}
          <div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-400 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                1
              </div>
              <div>
                <h3 className="text-xl font-bold text-blue-400 mb-2">
                  {device.deviceModel} auswählen
                </h3>
                <p className="text-gray-700 text-sm">
                  Entscheide dich einfach für eine der verfügbaren {device.deviceModel} Reparaturen.
                  Gewünschte Reparatur nicht vorhanden? <a href="https://www.phone-medic.de/kontakt" className="text-orange-500 hover:text-orange-600">Schreibe uns.</a>
                </p>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-400 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                2
              </div>
              <div>
                <h3 className="text-xl font-bold text-blue-400 mb-2">
                  Daten angeben
                </h3>
                <p className="text-gray-700 text-sm">
                  Gib deine Rechnungs- und Lieferdaten ein. Wir behandeln deine Daten
                  selbstverständlich vertraulich. Alles wird verschlüsselt übertragen.
                </p>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-400 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                3
              </div>
              <div>
                <h3 className="text-xl font-bold text-blue-400 mb-2">
                  Bezahloption wählen
                </h3>
                <p className="text-gray-700 text-sm">
                  Bezahle unkompliziert per PayPal, Sofortüberweisung oder Vorkasse.
                </p>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-400 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                4
              </div>
              <div>
                <h3 className="text-xl font-bold text-blue-400 mb-2">
                  Versandaufkleber erstellen
                </h3>
                <p className="text-gray-700 text-sm">
                  Du erhältst umgehend ein DHL Label. Gib anschließend das Paket in einer DHL Filiale ab oder lasse es abholen.
                </p>
              </div>
            </div>
          </div>

          {/* Step 5 */}
          <div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-400 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                5
              </div>
              <div>
                <h3 className="text-xl font-bold text-blue-400 mb-2">
                  Wir reparieren dein defektes {device.deviceModel}
                </h3>
                <p className="text-gray-700 text-sm">
                  Sobald dein Gerät die PhoneMedic Werkstatt erreicht, beginnen die Techniker
                  mit der Reparatur oder Fehlerdiagnose.
                </p>
              </div>
            </div>
          </div>

          {/* Step 6 */}
          <div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-blue-400 text-white rounded-full flex items-center justify-center text-2xl font-bold">
                6
              </div>
              <div>
                <h3 className="text-xl font-bold text-blue-400 mb-2">
                  Reparaturstatus einsehen
                </h3>
                <p className="text-gray-700 text-sm">
                  Im Kundenmenü kannst du in Echtzeit Informationen zum{' '}
                  <a href="https://www.phone-medic.de/track" className="text-orange-500 hover:text-orange-600">
                    Reparaturverlauf
                  </a>{' '}
                  abrufen und nach abgeschlossener Reparatur das DHL-Paket verfolgen. Wir
                  informieren dich auch per E-Mail über Updates zur Reparatur.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Express Repair Modal */}
      {selectedRepair && (
        <ExpressRepairModal
          isOpen={showExpressModal}
          onClose={() => setShowExpressModal(false)}
          onConfirm={handleExpressDecision}
          repair={selectedRepair}
        />
      )}
    </div>
  )
}

export default RepairSelector